import { Link } from 'react-router-dom';
import {
  CompanionPageTitle,
  CompanionPublicPage,
  CompanionSection,
  SupportEmail,
} from '../components/companion/CompanionPublicPage';

const UPDATED = 'October 19, 2026';

/**
 * Public privacy policy for HOWIE Companion: the Chrome extension and the Mac
 * and Windows app. Linked from the Chrome Web Store listing, the download page
 * and the heads-up line users paste into meetings (#in-a-meeting), so it must
 * stay reachable without signing in. Every sentence here must match what the
 * code does; store/privacy.md and store/listing.md say the same things.
 */
export default function CompanionPrivacyPage() {
  return (
    <CompanionPublicPage>
      <CompanionPageTitle>HOWIE Companion privacy policy</CompanionPageTitle>
      <p className="mt-2 text-sm text-landing-muted">
        Last updated {UPDATED}. Applies to the HOWIE Companion Chrome extension and the HOWIE Companion app for Mac and
        Windows. If you were in a meeting with someone using Howie, start{' '}
        <a className="underline" href="#in-a-meeting">here</a>.
      </p>

      <CompanionSection title="What HOWIE Companion is">
        <p>
          HOWIE Companion brings Howie, the HOW Institute&apos;s AI companion for moral leadership, into your meetings and
          your browser. When you ask, it listens to the meeting you are in or reads the page you are on, and answers in a
          private panel. Howie doesn&apos;t join your meeting. Its advice appears only on your screen. If you share your
          screen, others may see Howie&apos;s panel.
        </p>
      </CompanionSection>

      <CompanionSection title="What we collect, and when">
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong>Your Google email and name</strong>, when you sign in. We use them only to match you to your Howie
            account. We request no other Google data.
          </li>
          <li>
            <strong>Meeting audio</strong>, only while Howie is listening. In Chrome, that starts when you press
            Start and covers the meeting tab. In the desktop app, it starts when you press Start for a meeting and
            confirm the people there know; on a Mac it covers only the meeting app&apos;s sound, and on
            Windows it covers the sound your computer plays, which may include other sounds such as music or a video. The
            audio goes straight from your computer to our speech-to-text provider over an encrypted connection and is
            turned into text as it arrives. Lightning Studios never receives the audio, and nothing is recorded.
          </li>
          <li>
            <strong>Your microphone</strong>, so Howie can hear you. It is on by default and you can turn it off: in the
            side panel in Chrome, on the listening pill in the desktop app. The desktop app&apos;s microphone stays on while
            you are muted in Zoom or Teams, so Howie also hears anyone in the room with you.
          </li>
          <li>
            <strong>The names of the people in the meeting</strong>, so the transcript can say who is speaking. In Chrome
            they come from the meeting page. In the Zoom and Teams apps they come from the apps&apos; accessibility
            interfaces (Accessibility on a Mac, UI Automation on Windows), which describe the participant list and who is
            speaking. Howie does not record your screen and does not read other apps.
          </li>
          <li>
            <strong>Page content</strong>, only from the tab you ask about and only at the moment you ask: the title and
            address of the page, text you selected, visible page text, or the Gmail thread you are reading.
          </li>
          <li>
            <strong>Your questions to Howie and Howie&apos;s answers.</strong>
          </li>
        </ul>
      </CompanionSection>

      <CompanionSection id="what-we-keep" title="What we keep">
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong>The live transcript stays in memory on your computer.</strong> In Chrome it is kept while the meeting
            tab is open and cleared when you close the tab, start listening again, or quit Chrome. The desktop app keeps it
            for the meeting and clears it once your take is written or when you choose Stop and clear. We never write it to
            disk.
          </li>
          <li>
            <strong>When you ask Howie something</strong>, the recent part of the transcript (in the desktop app, the
            meeting so far) goes with your question to Howie to answer you. After the answer, we keep only the lines Howie
            cites as evidence, up to six, together with the names of the people in the meeting. The rest of what Howie read
            is not kept. When you ask about a page or an email, we keep the text Howie read from it.
          </li>
          <li>
            <strong>We keep the advice Howie gave you</strong> and your questions, so you can review your sessions in the
            Howie app.
          </li>
          <li>
            <strong>Meeting takes.</strong> At the end of a meeting in the desktop app, the meeting&apos;s transcript is sent
            once to Anthropic to write your take: a summary, what was committed to, and how you lived the HOW. The take is
            stored with your session. It can quote short moments and name people and what they said they would do. The
            transcript it was written from is not kept.
          </li>
          <li>
            <strong>Forget the last 2 minutes.</strong> In the desktop app, this removes the last two minutes of the
            transcript from your computer, so those lines are never sent with a later question or used for your take. Lines
            already sent with a question stay with that answer in History until you delete the session.
          </li>
          <li>
            <strong>How long.</strong> Everything above stays until you delete the session in the Howie app. Deleting a
            session removes its advice, cited lines, names and take. To delete your account and everything stored with it,
            email <SupportEmail />.
          </li>
          <li>
            <strong>On your computer.</strong> The desktop app writes no audio, transcript or Ask history to disk. The
            Chrome extension saves your recent questions and Howie&apos;s answers in Chrome&apos;s local storage so the side
            panel can show them again.
          </li>
        </ul>
      </CompanionSection>

      <CompanionSection title="Who sees it">
        <p>
          Your data is visible only to you in your Howie account and to the Lightning Studios team operating the service.
          We use Anthropic to generate Howie&apos;s answers and meeting takes, under terms that prohibit it from using your
          data to train its models. Meeting audio is streamed to Deepgram only to turn speech into text, and every
          connection opts out of Deepgram&apos;s model-improvement program. The text of a
          question you type to Howie (never the page or its title) is also sent to TypeSafe, a classification service that
          helps Howie choose how to respond. In the desktop app and the Chrome extension, when HOWIE cannot tell who an
          unnamed voice in a meeting is, the lines that voice said (and the line either side of each), the participant names HOWIE saw and your name
          are sent to TypeSafe to suggest who is speaking; nothing is stored there and no audio is sent. TypeSafe&apos;s
          terms prohibit it from using your data to train its models. We do not sell your data, share it with advertisers,
          or use it for anything other than giving you Howie&apos;s coaching.
        </p>
      </CompanionSection>

      <CompanionSection title="Your choices">
        <ul className="list-disc space-y-2 pl-5">
          <li>
            Listening starts only when you start it. In Chrome it stops when you press Stop or close the tab; in the
            desktop app, when you press Stop or the meeting ends. Howie never starts listening on its own.
          </li>
          <li>Turn your microphone off at any time: in the side panel in Chrome, on the listening pill in the desktop app.</li>
          <li>
            Stop and clear, in the desktop app, stops listening and drops what Howie heard in this meeting. No take is
            written.
          </li>
          <li>Delete any session from the Howie app at any time.</li>
          <li>Sign out from the side panel or the desktop app to remove your Google sign-in from this computer.</li>
          <li>
            Telling the people you meet with is your responsibility. Many places require it, including California, Florida,
            Illinois, Maryland, Massachusetts, Pennsylvania and Washington, and the EU and UK under the GDPR. Some also
            require their agreement. Howie gives you a line to paste into the meeting chat, but it never posts anything for
            you.
          </li>
        </ul>
      </CompanionSection>

      <CompanionSection id="in-a-meeting" title="If you were in a meeting with someone using Howie">
        <p>
          Howie is a private AI coach. It doesn&apos;t join meetings as a bot and doesn&apos;t post anything in them, so you
          won&apos;t see it unless the person using it tells you. We ask every user to tell the people they meet with.
        </p>
        <p>
          While they have it on, Howie turns the meeting&apos;s audio into text so it can help them. The audio goes from
          their computer to our speech-to-text provider and is turned into text as it arrives. Howie does not record it,
          and Lightning Studios never receives it. The person using Howie keeps the advice Howie gave them, the transcript lines Howie cited (up to six per answer),
          the names of the people in the meeting, and a short take on the meeting. These can include your name and things
          you said. They are visible only to that person and to the Lightning Studios team that operates the service, and
          they stay until that person deletes them.
        </p>
        <p>
          When Howie cannot tell who is speaking, the lines that voice said (and the line either side of each) and the names
          of the people in the meeting go to TypeSafe, a classification service, only to suggest who is speaking. TypeSafe
          keeps nothing and receives no audio. Howie marks the suggested name as likely.
        </p>
        <p>
          If you&apos;d rather not be transcribed, tell the person using Howie. They can stop it and clear what it heard.
          To ask us about data that includes you, write to <SupportEmail />.
        </p>
      </CompanionSection>

      <CompanionSection id="it-admins" title="For IT admins">
        <p className="text-sm text-landing-muted">
          Answers marked <Tbd /> are still being settled for the pilot. Write to <SupportEmail /> if you need one before we
          publish it.
        </p>
        <Faq question="Does it join meetings as a bot?">
          No. It runs on the user&apos;s computer and hears what that user hears. It posts nothing in the meeting.
        </Faq>
        <Faq question="What permissions does it request, and when?">
          <ul className="list-disc space-y-1 pl-5">
            <li>Microphone: the first time the user starts listening.</li>
            <li>System Audio Recording (macOS 14.4 or later): at the first meeting in a desktop meeting app.</li>
            <li>Accessibility (Mac, optional): to see when a call starts and who is speaking in Zoom and Teams.</li>
            <li>Not requested: Screen Recording, Calendar, Camera, Input Monitoring.</li>
            <li>Windows: the microphone and the sound the computer plays. No administrator rights; installs per user.</li>
          </ul>
        </Faq>
        <Faq question="What leaves the device, and to whom?">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Audio goes directly to Deepgram over TLS with a 60-second token, opted out of Deepgram&apos;s
              model-improvement data sharing. Whether Deepgram keeps any audio under our agreement: <Tbd />.
            </li>
            <li>
              Text (questions, recent transcript lines, the meeting transcript once for the take) goes to the Howie service
              on Railway, and from there to Anthropic to write Howie&apos;s answers and takes.
            </li>
            <li>
              The text of a typed question goes to TypeSafe, to route it; in the desktop app and the Chrome extension, so do
              the lines of a meeting voice HOWIE could not name (with the line either side), the participant names and your name, to suggest who
              is speaking.
            </li>
            <li>Sign-in uses Google OAuth, with email and profile scopes only.</li>
            <li>Processing in the EU: <Tbd />.</li>
          </ul>
        </Faq>
        <Faq question="What is stored, where, and for how long?">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Questions and answers, up to six transcript lines Howie cites per answer, the names of the people in the
              meeting, and meeting takes.
            </li>
            <li>Stored in Supabase, tied to the user. Region: <Tbd />.</li>
            <li>Kept until the user deletes the session; deleting a session deletes everything stored with it.</li>
            <li>Account deletion is by email to support today.</li>
            <li>
              On the device: the desktop app writes no audio, transcript or Ask history to disk. The Chrome extension keeps
              recent questions and answers in Chrome&apos;s local storage.
            </li>
          </ul>
        </Faq>
        <Faq question="Is anything used to train AI?">
          Not by us. Anthropic&apos;s and TypeSafe&apos;s terms prohibit training on this data, and every Deepgram
          connection opts out of its model-improvement data sharing.
        </Faq>
        <Faq question="How are other participants told? Can we enforce a notice?">
          Howie doesn&apos;t post a notice. Users are told that telling people is their responsibility and get a line to
          copy. Admins can add Zoom&apos;s custom meeting disclaimer or Teams&apos; custom recording notice. A managed
          &ldquo;require notice&rdquo; setting is not in this version.
        </Faq>
        <Faq question="Does it hear the user while they are muted in the call, or other apps?">
          <ul className="list-disc space-y-1 pl-5">
            <li>It hears the microphone while the user is muted in Zoom or Teams; the pill has its own microphone switch.</li>
            <li>On a Mac it hears only the meeting app&apos;s sound.</li>
            <li>On Windows it may hear other sounds the computer plays.</li>
          </ul>
        </Faq>
        <Faq question="Does it read the screen?">
          No screen capture. With Accessibility on a Mac, or UI Automation on Windows, it reads the meeting app&apos;s
          window controls and participant names. It never reads other apps.
        </Faq>
        <Faq question="How is it signed and updated?">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Mac: Developer ID (Team <code>M8846YMQY6</code>), notarized. Update host: <Tbd />.
            </li>
            <li>
              Windows: signer <Tbd /> (Azure Artifact Signing or an OV certificate), or unsigned for the pilot.
            </li>
            <li>Turning off automatic updates by policy: <Tbd />.</li>
          </ul>
        </Faq>
        <Faq question="Which network endpoints should we allow or block?">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <code>howie-sdk-service-production.up.railway.app</code>
            </li>
            <li>
              <code>api.deepgram.com</code> (secure WebSocket)
            </li>
            <li>
              <code>accounts.google.com</code>, <code>oauth2.googleapis.com</code>, <code>www.googleapis.com</code>
            </li>
            <li>The update host: <Tbd />.</li>
          </ul>
        </Faq>
        <Faq question="How do we deploy it with MDM?">
          Mac: a disk image today; a signed installer package: <Tbd />. Windows: a per-user installer; an MSI or per-machine
          install: <Tbd />.
        </Faq>
        <Faq question="How do we block it?">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Mac: block by Team ID <code>M8846YMQY6</code> or the bundle ID (bundle ID: <Tbd />). A PPPC profile can deny
              the microphone; whether one can deny System Audio Recording: <Tbd />.
            </li>
            <li>
              Windows: an AppLocker or WDAC publisher rule once the app is signed; until then, a path rule on{' '}
              <code>%LOCALAPPDATA%\Programs\HOWIE Companion</code>.
            </li>
            <li>Chrome: <code>ExtensionInstallBlocklist</code> with the extension ID.</li>
            <li>Network: block the endpoints above.</li>
          </ul>
        </Faq>
        <Faq question="How does a user uninstall it, and what is left behind?">
          See the{' '}
          <Link className="underline" to="/companion/download">
            download page
          </Link>
          . Data on our servers stays until the user deletes it in History or asks us to.
        </Faq>
        <Faq question="Who is the controller of the data?">
          <Tbd />, with counsel. The services that process it today are Anthropic, Deepgram, TypeSafe, Supabase, Railway
          and Google sign-in.
        </Faq>
        <Faq question="Which accounts work?">
          Google accounts whose email matches a Howie profile. No SSO in this version.
        </Faq>
      </CompanionSection>

      <CompanionSection title="Contact">
        <p>
          Lightning Studios, Inc. · <SupportEmail /> ·{' '}
          <Link className="underline" to="/companion/download">
            Download HOWIE Companion
          </Link>
        </p>
      </CompanionSection>
    </CompanionPublicPage>
  );
}

function Tbd() {
  return (
    <span className="rounded bg-how-rose px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-how-space-deep">
      TBD
    </span>
  );
}

function Faq({ question, children }: { question: string; children: React.ReactNode }) {
  return (
    <div className="mt-5">
      <h3 className="font-semibold text-how-space-deep">{question}</h3>
      <div className="mt-1">{children}</div>
    </div>
  );
}
